/**
 * Email Verification Page
 *
 * Shown to authenticated users whose email is not yet verified.
 * Sits between authentication and MFA enrollment in the guard chain:
 *   Login → EmailVerificationGuard → MFAGuard → App
 *
 * Flow:
 *   1. User sees message that a verification email was sent
 *   2. User clicks link in email (opens in new tab / same browser)
 *   3. User clicks "I've verified my email" → we call user.reload()
 *   4. If emailVerified === true, guard passes through to MFA enrollment
 *   5. If not, show "not verified yet" message
 */

import { useState, useEffect, useCallback } from 'react';
import { sendEmailVerification } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import {
  Mail,
  Check,
  AlertCircle,
  Loader2,
  RefreshCw,
} from 'lucide-react';

const RESEND_COOLDOWN_SECONDS = 60;

export function EmailVerification(): JSX.Element {
  const { user, signOut } = useAuth();

  const [isChecking, setIsChecking] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  // Cooldown timer for resend button
  useEffect(() => {
    if (cooldownRemaining <= 0) return;

    const timer = setInterval(() => {
      setCooldownRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldownRemaining]);

  // Send initial verification email on mount if user hasn't verified.
  // This is a silent best-effort send — errors are logged but NOT displayed,
  // since the user hasn't taken any action yet. They can always click Resend.
  useEffect(() => {
    if (user && !user.emailVerified) {
      sendEmailVerification(user)
        .then(() => {
          setCooldownRemaining(RESEND_COOLDOWN_SECONDS);
        })
        .catch((err) => {
          console.warn('Auto-send verification email failed (silent):', err);
        });
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getErrorMessage = (err: any): string => {
    const code = err?.code || '';
    switch (code) {
      case 'auth/too-many-requests':
        return 'Too many attempts. Please wait a few minutes and try again.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection and try again.';
      default:
        return 'Something went wrong. Please try again.';
    }
  };

  const handleResendEmail = useCallback(async () => {
    if (!user || cooldownRemaining > 0) return;

    setError(null);
    setResendSuccess(false);
    setIsResending(true);

    try {
      await sendEmailVerification(user);
      setResendSuccess(true);
      setCooldownRemaining(RESEND_COOLDOWN_SECONDS);

      // Clear success message after a few seconds
      setTimeout(() => setResendSuccess(false), 4000);
    } catch (err: any) {
      console.error('Error sending verification email:', err);
      setError(getErrorMessage(err));
    } finally {
      setIsResending(false);
    }
  }, [user, cooldownRemaining]);

  const handleCheckVerification = async () => {
    if (!user) return;

    setError(null);
    setIsChecking(true);

    try {
      await user.reload();

      if (user.emailVerified) {
        // Force a full reload so auth state + guards re-evaluate
        window.location.href = '/';
      } else {
        setError('Email not verified yet. Please check your inbox and click the verification link.');
      }
    } catch (err: any) {
      console.error('Error checking verification status:', err);
      setError(getErrorMessage(err));
    } finally {
      setIsChecking(false);
    }
  };

  const maskedEmail = user?.email
    ? (() => {
        const [local, domain] = user.email.split('@');
        if (local.length <= 2) return user.email;
        return `${local[0]}${'•'.repeat(Math.min(local.length - 2, 6))}${local[local.length - 1]}@${domain}`;
      })()
    : '';

  return (
    <div className="min-h-screen flex items-center justify-center bg-airy">
      <div className="max-w-md w-full space-y-6 p-8 bg-white rounded-lg shadow-card border border-soft-neutral">
        {/* Header */}
        <div className="text-center">
          <div className="w-16 h-16 bg-evergreen/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-evergreen" />
          </div>
          <h1 className="text-2xl font-bold text-ink">Verify Your Email</h1>
          <p className="text-muted text-sm mt-2">
            We've sent a verification link to{' '}
            <span className="font-semibold text-ink">{maskedEmail}</span>.
            Please check your inbox and click the link to continue.
          </p>
        </div>

        {/* Instructions */}
        <div className="bg-airy rounded-lg p-4 space-y-2">
          <p className="text-sm text-slate-ink font-medium">Steps to verify:</p>
          <ol className="text-sm text-muted space-y-1.5 list-decimal list-inside">
            <li>Open your email inbox</li>
            <li>Find the email from Hesvara (check spam/junk folder)</li>
            <li>Click the verification link in the email</li>
            <li>Return here and click "I've verified my email"</li>
          </ol>
        </div>

        {/* Status messages */}
        {resendSuccess && (
          <div className="alert alert-success flex items-center gap-2">
            <Check className="w-5 h-5 flex-shrink-0" />
            Verification email sent! Check your inbox.
          </div>
        )}

        {error && (
          <div className="alert alert-error flex items-center gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          {/* Primary: Check verification */}
          <button
            onClick={handleCheckVerification}
            className="btn-primary w-full flex items-center justify-center gap-2"
            disabled={isChecking}
          >
            {isChecking ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Checking...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                I've Verified My Email
              </>
            )}
          </button>

          {/* Secondary: Resend email */}
          <button
            onClick={handleResendEmail}
            className="btn-secondary w-full flex items-center justify-center gap-2"
            disabled={isResending || cooldownRemaining > 0}
          >
            {isResending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sending...
              </>
            ) : cooldownRemaining > 0 ? (
              <>
                <RefreshCw className="w-4 h-4" />
                Resend in {cooldownRemaining}s
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                Resend Verification Email
              </>
            )}
          </button>
        </div>

        {/* Sign out link */}
        <div className="text-center pt-2">
          <button
            onClick={signOut}
            className="text-sm text-muted hover:text-slate-ink hover:underline"
          >
            Sign out and use a different account
          </button>
        </div>
      </div>
    </div>
  );
}

export default EmailVerification;
